import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'

interface ResizablePanelProps {
  children: [ReactNode, ReactNode]
  /** Right panel width in px, kept within these bounds. */
  minRightWidth: number
  maxRightWidth: number
  /** Share of the window the right panel takes until the user sets a width. */
  defaultRightShare?: number
  storageKey?: string
  className?: string
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

function readSaved(key: string | undefined): number | null {
  if (!key) return null
  try {
    const saved = Number(localStorage.getItem(key))
    return Number.isFinite(saved) && saved > 0 ? saved : null
  } catch {
    return null
  }
}

export default function ResizablePanel({
  children,
  minRightWidth,
  maxRightWidth,
  defaultRightShare = 0.25,
  storageKey,
  className = '',
}: ResizablePanelProps) {
  const [userWidth, setUserWidth] = useState<number | null>(() => readSaved(storageKey))
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const rightWidth = clamp(userWidth ?? windowWidth * defaultRightShare, minRightWidth, maxRightWidth)

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    setIsDragging(true)
  }
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const container = containerRef.current
    if (!isDragging || !container) return
    const next = clamp(container.getBoundingClientRect().right - e.clientX, minRightWidth, maxRightWidth)
    setUserWidth(next)
  }
  const onPointerUp = () => {
    if (!isDragging) return
    setIsDragging(false)
    if (!storageKey || userWidth == null) return
    try {
      localStorage.setItem(storageKey, String(Math.round(userWidth)))
    } catch {
      // Private mode: the width just isn't remembered.
    }
  }

  const dividerWidth = 2

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: rightWidth + dividerWidth / 2,
          overflow: 'hidden',
        }}
      >
        {children[0]}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat"
        aria-valuenow={Math.round(rightWidth)}
        aria-valuemin={minRightWidth}
        aria-valuemax={maxRightWidth}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: rightWidth - dividerWidth / 2,
          width: `${dividerWidth}px`,
          cursor: 'col-resize',
          touchAction: 'none',
          backgroundColor: isDragging ? 'var(--color-primary)' : 'var(--color-line)',
          transition: 'background-color 0.15s',
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = 'var(--color-primary)'
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = 'var(--color-line)'
          }
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: rightWidth - dividerWidth / 2,
          overflow: 'hidden',
        }}
      >
        {children[1]}
      </div>
    </div>
  )
}
